import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  component: React.ComponentType<OnboardingStepProps>;
  isOptional?: boolean;
}

interface OnboardingStepProps {
  onNext: (data?: any) => void;
  onPrevious: () => void;
  onSkip?: () => void;
  data: any;
  setData: (data: any) => void;
}

interface OnboardingData {
  businessInfo: {
    business_name: string;
    business_type?: string; // Optional, not used for pack selection
    industry?: string;
    website?: string;
    phone?: string;
    address?: string;
  };
  branding: {
    logo?: string;
    primary_color: string;
    secondary_color: string;
    custom_domain?: string;
  };
  integrations: {
    email_provider: string;
    sms_provider: string;
    google_places_id?: string;
  };
  preferences: {
    notification_frequency: string;
    timezone: string;
    language: string;
  };
}

// Step 1: Business Information (Additional Details)
const BusinessInfoStep: React.FC<OnboardingStepProps> = ({ onNext, data, setData }) => {
  const { tenant } = useAuth();
  const [formData, setFormData] = useState(data.businessInfo || {
    business_name: tenant?.name || '',
    website: '',
    phone: '',
    address: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setData({ ...data, businessInfo: formData });
    onNext();
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Complete your business profile</h2>
        <p className="text-lg text-gray-600">Add additional details to help us personalize your experience</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="business_name" className="block text-sm font-medium text-gray-700 mb-2">
            Business Name
          </label>
          <input
            type="text"
            id="business_name"
            value={formData.business_name}
            onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Enter your business name"
          />
          <p className="mt-1 text-sm text-gray-500">Update your business name if needed</p>
        </div>

        <div>
          <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
            Website (Optional)
          </label>
          <input
            type="url"
            id="website"
            value={formData.website}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="https://yourwebsite.com"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number (Optional)
          </label>
          <input
            type="tel"
            id="phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="(555) 123-4567"
          />
        </div>

        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
            Business Address (Optional)
          </label>
          <textarea
            id="address"
            rows={3}
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Enter your business address"
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
};

// Step 2: Branding
const BrandingStep: React.FC<OnboardingStepProps> = ({ onNext, onPrevious, data, setData }) => {
  const [formData, setFormData] = useState(data.branding || {
    primary_color: '#3B82F6',
    secondary_color: '#1E40AF',
    custom_domain: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setData({ ...data, branding: formData });
    onNext();
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Customize your branding</h2>
        <p className="text-lg text-gray-600">Set up your brand colors and domain</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="primary_color" className="block text-sm font-medium text-gray-700 mb-2">
            Primary Color
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="color"
              id="primary_color"
              value={formData.primary_color}
              onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
              className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
            />
            <input
              type="text"
              value={formData.primary_color}
              onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="#3B82F6"
            />
          </div>
        </div>

        <div>
          <label htmlFor="secondary_color" className="block text-sm font-medium text-gray-700 mb-2">
            Secondary Color
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="color"
              id="secondary_color"
              value={formData.secondary_color}
              onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
              className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
            />
            <input
              type="text"
              value={formData.secondary_color}
              onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="#1E40AF"
            />
          </div>
        </div>

        <div>
          <label htmlFor="custom_domain" className="block text-sm font-medium text-gray-700 mb-2">
            Custom Domain (Optional)
          </label>
          <input
            type="text"
            id="custom_domain"
            value={formData.custom_domain}
            onChange={(e) => setFormData({ ...formData, custom_domain: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="yourdomain.com"
          />
          <p className="mt-2 text-sm text-gray-500">
            You can set up a custom domain later in settings
          </p>
        </div>

        <div className="flex justify-between">
          <button
            type="button"
            onClick={onPrevious}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
          >
            Back
          </button>
          <button
            type="submit"
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
};

// Step 3: Integrations
const IntegrationsStep: React.FC<OnboardingStepProps> = ({ onNext, onPrevious, onSkip, data, setData }) => {
  const [formData, setFormData] = useState(data.integrations || {
    email_provider: 'postmark',
    sms_provider: 'twilio',
    google_places_id: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setData({ ...data, integrations: formData });
    onNext();
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Set up integrations</h2>
        <p className="text-lg text-gray-600">Connect your email and SMS providers</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="email_provider" className="block text-sm font-medium text-gray-700 mb-2">
            Email Provider
          </label>
          <select
            id="email_provider"
            value={formData.email_provider}
            onChange={(e) => setFormData({ ...formData, email_provider: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="postmark">Postmark</option>
            <option value="sendgrid">SendGrid</option>
            <option value="ses">Amazon SES</option>
          </select>
        </div>

        <div>
          <label htmlFor="sms_provider" className="block text-sm font-medium text-gray-700 mb-2">
            SMS Provider
          </label>
          <select
            id="sms_provider"
            value={formData.sms_provider}
            onChange={(e) => setFormData({ ...formData, sms_provider: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="twilio">Twilio</option>
            <option value="aws_sns">AWS SNS</option>
            <option value="none">None</option>
          </select>
        </div>

        <div>
          <label htmlFor="google_places_id" className="block text-sm font-medium text-gray-700 mb-2">
            Google Places ID (Optional)
          </label>
          <input
            type="text"
            id="google_places_id"
            value={formData.google_places_id}
            onChange={(e) => setFormData({ ...formData, google_places_id: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4"
          />
          <p className="mt-2 text-sm text-gray-500">
            You can find this in your Google My Business account
          </p>
        </div>

        <div className="flex justify-between">
          <button
            type="button"
            onClick={onPrevious}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
          >
            Back
          </button>
          <div className="space-x-4">
            {onSkip && (
              <button
                type="button"
                onClick={onSkip}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
              >
                Skip for now
              </button>
            )}
            <button
              type="submit"
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// Step 4: Preferences
const PreferencesStep: React.FC<OnboardingStepProps> = ({ onNext, onPrevious, data, setData }) => {
  const [formData, setFormData] = useState(data.preferences || {
    notification_frequency: 'daily',
    timezone: 'America/New_York',
    language: 'en',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setData({ ...data, preferences: formData });
    onNext();
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Set your preferences</h2>
        <p className="text-lg text-gray-600">Configure your notification and display settings</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="notification_frequency" className="block text-sm font-medium text-gray-700 mb-2">
            Notification Frequency
          </label>
          <select
            id="notification_frequency"
            value={formData.notification_frequency}
            onChange={(e) => setFormData({ ...formData, notification_frequency: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="immediate">Immediate</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="never">Never</option>
          </select>
        </div>

        <div>
          <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-2">
            Timezone
          </label>
          <select
            id="timezone"
            value={formData.timezone}
            onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="America/New_York">Eastern Time</option>
            <option value="America/Chicago">Central Time</option>
            <option value="America/Denver">Mountain Time</option>
            <option value="America/Los_Angeles">Pacific Time</option>
            <option value="Europe/London">London</option>
            <option value="Europe/Paris">Paris</option>
            <option value="Asia/Tokyo">Tokyo</option>
          </select>
        </div>

        <div>
          <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-2">
            Language
          </label>
          <select
            id="language"
            value={formData.language}
            onChange={(e) => setFormData({ ...formData, language: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="en">English</option>
            <option value="es">Spanish</option>
            <option value="fr">French</option>
            <option value="de">German</option>
          </select>
        </div>

        <div className="flex justify-between">
          <button
            type="button"
            onClick={onPrevious}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
          >
            Back
          </button>
          <button
            type="submit"
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
          >
            Complete Setup
          </button>
        </div>
      </form>
    </div>
  );
};

// Completion Step
const CompletionStep: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
  return (
    <div className="max-w-2xl mx-auto text-center">
      <div className="mb-8">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
          <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Welcome to ReputationManager!</h2>
        <p className="text-lg text-gray-600 mb-8">
          Your account has been set up successfully. You can now start managing your online reputation.
        </p>
      </div>

      <div className="space-y-4 mb-8">
        <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
          <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>Account created and verified</span>
        </div>
        <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
          <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>Business information configured</span>
        </div>
        <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
          <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>Branding preferences set</span>
        </div>
      </div>

      <button
        onClick={onComplete}
        className="px-8 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors text-lg font-medium"
      >
        Go to Dashboard
      </button>
    </div>
  );
};

// Main Onboarding Wizard Component
export const OnboardingWizard: React.FC = () => {
  const navigate = useNavigate();
  const { user, tenant } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({
    businessInfo: {
      business_name: tenant?.name || '',
      business_type: '', // Not used anymore, kept for type compatibility
      industry: '',
      website: '',
      phone: '',
      address: '',
    },
    branding: {
      primary_color: '#3B82F6',
      secondary_color: '#1E40AF',
      custom_domain: '',
    },
    integrations: {
      email_provider: 'postmark',
      sms_provider: 'twilio',
      google_places_id: '',
    },
    preferences: {
      notification_frequency: 'daily',
      timezone: 'America/New_York',
      language: 'en',
    },
  });

  const steps: OnboardingStep[] = [
    {
      id: 'business-info',
      title: 'Business Details',
      description: 'Complete your business profile',
      component: BusinessInfoStep,
    },
    {
      id: 'branding',
      title: 'Branding',
      description: 'Customize your appearance',
      component: BrandingStep,
    },
    {
      id: 'integrations',
      title: 'Integrations',
      description: 'Connect your services',
      component: IntegrationsStep,
      isOptional: true,
    },
    {
      id: 'preferences',
      title: 'Preferences',
      description: 'Set your preferences',
      component: PreferencesStep,
    },
  ];

  const handleNext = (stepData?: any) => {
    if (stepData) {
      setOnboardingData({ ...onboardingData, ...stepData });
    }
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Complete onboarding
      completeOnboarding();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeOnboarding();
    }
  };

  const completeOnboarding = async () => {
    try {
      // Update tenant with onboarding data (business name only, no pack change)
      if (tenant && onboardingData.businessInfo.business_name) {
        await api.tenant.update({
          name: onboardingData.businessInfo.business_name,
          // Note: vertical_pack is NOT updated here - it was set during registration
        });
      }

      // Update branding
      if (onboardingData.branding) {
        await api.branding.update({
          primary_color: onboardingData.branding.primary_color,
          secondary_color: onboardingData.branding.secondary_color,
          custom_domain: onboardingData.branding.custom_domain,
        });
      }

      // Navigate to dashboard
      navigate('/');
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      // Still navigate to dashboard even if some updates fail
      navigate('/');
    }
  };

  const handleComplete = () => {
    navigate('/');
  };

  // Show completion step
  if (currentStep >= steps.length) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <CompletionStep onComplete={handleComplete} />
      </div>
    );
  }

  const currentStepData = steps[currentStep];
  const StepComponent = currentStepData.component;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Bar */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-bold text-gray-900">Welcome to ReputationManager</h1>
              <span className="text-sm text-gray-500">
                Step {currentStep + 1} of {steps.length}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <StepComponent
          onNext={handleNext}
          onPrevious={handlePrevious}
          onSkip={currentStepData.isOptional ? handleSkip : undefined}
          data={onboardingData}
          setData={setOnboardingData}
        />
      </div>
    </div>
  );
};
